/**
 * useSetIbsChannelState -- admin mutation for the per-(IBS, channel)
 * resilience POST endpoint (P18.2 / #500).
 *
 * Wraps `POST /v1/admin/twin/ibs-channels/{ibs_id}/{channel_id}/
 * resilience`. On success, invalidates the channel heatmap cache so
 * the cell repaints immediately.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { channelHeatmapKeys } from "shared/api/queryClient";

export interface SetIbsChannelStateInput {
  ibs_id: string;
  channel_id: string;
  state: "red" | "amber" | "green" | "unknown";
  reasons: { message: string }[];
}

export interface SetIbsChannelStateResult {
  id: string;
  entity_id: string;
  state: string;
  captured_at: string;
  captured_by: string;
}

export function useSetIbsChannelState(): UseMutationResult<
  SetIbsChannelStateResult,
  ApiError,
  SetIbsChannelStateInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ibs_id, channel_id, state, reasons }) =>
      api.post<SetIbsChannelStateResult>(
        `/v1/admin/twin/ibs-channels/${encodeURIComponent(ibs_id)}/${encodeURIComponent(channel_id)}/resilience`,
        { state, reasons },
      ),
    onSuccess: () => {
      // Repaint the heatmap immediately -- all as_of variants
      // share the same root key.
      void qc.invalidateQueries({ queryKey: channelHeatmapKeys.all });
    },
  });
}
